import { useEffect, useMemo, useState } from "react";

function preferredVariant(variants) {
  return (
    variants.find((variant) => variant.stockStatus === "in_stock") ||
    variants.find((variant) => variant.stockStatus === "backorder") ||
    variants[0] ||
    null
  );
}

function optionGroups(variants) {
  const groups = new Map();
  variants.forEach((variant) => {
    Object.entries(variant.options || {}).forEach(([name, value]) => {
      if (!groups.has(name)) groups.set(name, []);
      if (!groups.get(name).includes(value)) groups.get(name).push(value);
    });
  });
  return Array.from(groups, ([name, values]) => ({ name, values }));
}

export default function VariantPicker({ variants = [], onChange }) {
  const initialVariant = useMemo(() => preferredVariant(variants), [variants]);
  const [selectedVariantId, setSelectedVariantId] = useState(
    initialVariant?.id || null
  );
  const groups = useMemo(() => optionGroups(variants), [variants]);
  const selectedVariant =
    variants.find((variant) => variant.id === selectedVariantId) ||
    initialVariant;

  useEffect(() => {
    setSelectedVariantId(initialVariant?.id || null);
  }, [initialVariant]);

  useEffect(() => {
    onChange(selectedVariant || null);
  }, [onChange, selectedVariant]);

  const selectOption = (optionName, optionValue) => {
    const nextOptions = {
      ...(selectedVariant?.options || {}),
      [optionName]: optionValue,
    };
    const match = variants.find((variant) =>
      Object.entries(nextOptions).every(
        ([name, value]) => variant.options?.[name] === value
      )
    );
    if (match) setSelectedVariantId(match.id);
  };

  const optionIsAvailable = (optionName, optionValue) => {
    const selectedOptions = selectedVariant?.options || {};
    return variants.some(
      (variant) =>
        variant.options?.[optionName] === optionValue &&
        Object.entries(selectedOptions).every(
          ([name, value]) =>
            name === optionName || variant.options?.[name] === value
        )
    );
  };

  if (!selectedVariant) return null;

  if (groups.length === 0 && variants.length > 1) {
    return (
      <fieldset className="variant-picker">
        <legend>Escolha uma opção</legend>
        <div className="variant-picker__cards">
          {variants.map((variant) => (
            <button
              type="button"
              className={
                variant.id === selectedVariant.id
                  ? "variant-card variant-card--selected"
                  : "variant-card"
              }
              onClick={() => setSelectedVariantId(variant.id)}
              key={variant.id}
            >
              <span>{variant.title}</span>
              <small>
                {variant.stockStatus === "out_of_stock"
                  ? "Indisponível"
                  : "Disponível"}
              </small>
            </button>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <div className="variant-picker">
      {groups.map((group) => (
        <fieldset key={group.name}>
          <legend>{group.name}</legend>
          <div className="variant-picker__values">
            {group.values.map((value) => {
              const selected = selectedVariant.options?.[group.name] === value;
              const available = optionIsAvailable(group.name, value);
              return (
                <button
                  type="button"
                  className={
                    selected
                      ? "variant-value variant-value--selected"
                      : "variant-value"
                  }
                  disabled={!available}
                  aria-pressed={selected}
                  onClick={() => selectOption(group.name, value)}
                  key={value}
                >
                  {value}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
